// Roll-up maths for the client / group / task financial panels.
//
// Pure functions, no React, no fetching. Every figure on ClientDetailPage,
// ClientGroupPage and TaskDetailPage comes from here, so the rules below are
// stated once and cannot drift between pages.

/**
 * Time entries in these statuses count toward a client's financial picture.
 *
 * An allowlist, not a denylist, and deliberately so. `rejected` has been sent
 * back and is not real work. `draft` counts since 2026-10-27 (owner): a file
 * is often invoiced the same afternoon someone booked time to it, before that
 * time is submitted, and leaving it out under-billed the client. (Spec 3.4
 * originally excluded drafts.)
 */
export const COUNTED_TIME_STATUSES = ['draft', 'submitted', 'approved'];

export const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

export function countedTimeEntries(entries) {
  return (entries || []).filter((t) => COUNTED_TIME_STATUSES.includes(t.status));
}

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

const cents = (n) => Math.round(n * 100) / 100;

// A draft is not a bill. Until it is issued it moves no figure here -- not
// WIP, not invoiced, not A/R -- so "how much is left to bill" never shrinks
// because someone saved a draft. (Owner's rule, 2026-10-26.)
export const isIssued = (inv) => ((inv && inv.status) || 'DRAFT') !== 'DRAFT';
export const issuedOnly = (invoices) => (invoices || []).filter(isIssued);

export const taxOf = (inv) => num(inv.gst) + num(inv.pst) + num(inv.hst);
const totalOf = (inv) => inv.total ?? inv.total_amount;

// FEES BILLED: the fee the invoice charged for the work, BEFORE any discount
// and before disbursements and tax. This is what clears WIP -- a discount is
// part of writing the time off, not time left to bill (owner, 2026-10-26: bill
// $3,800 of time, discount $500, and the WIP is gone; the $500 shows as the
// discount).
//
// Read from the total, not from `subtotal`: about 1,250 historical invoices had
// their header rewritten by fix_invoices.py, whose `subtotal` is the CSV's
// PRE-TAX figure (fee - discount + disbursements). The total and the taxes are
// what went out, so fees = total - tax + discount - disbursements, which for
// any invoice ClientZone computed itself is exactly `subtotal`. Same rule as
// pages/invoices/prefillFromInvoice.js feesOf(). No total to read -> subtotal.
//
// Must read `discount` (the serializer's computed DOLLAR discount), never
// `discount_value` (the raw input, whose meaning depends on discount_type):
// a 10% discount has discount_value 10, not $10.
export const feesBilledOf = (inv) => {
  const total = totalOf(inv);
  if (total === null || total === undefined || total === '') return num(inv.subtotal);
  return cents(num(total) - taxOf(inv) + num(inv.discount) - num(inv.disbursements));
};

// INVOICED (the tile) is NET FEES: fees billed less the discount, before tax
// and disbursements -- what the firm actually earned on the bill.
export const invoicedOf = (inv) => feesBilledOf(inv) - num(inv.discount);

// A/R is what the client actually owes you, so it IS tax-inclusive. A draft
// is owed by nobody.
export const arOf = (inv) => (
  isIssued(inv) && inv.status !== 'PAID' ? num(totalOf(inv)) : 0
);

/**
 * The fiscal year a date falls in, for a given client.
 *
 * TimeEntry and Invoice carry their own `year_end`. WriteOff does NOT — the model
 * has only client/date/amount/reason — so its year has to be derived, which is
 * what this is for.
 */
export function yearEndForDate(dateStr, isCorporate, fiscalMonthEnd) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  if (!isCorporate || !fiscalMonthEnd) return d.getFullYear();
  return d.getMonth() + 1 <= Number(fiscalMonthEnd) ? d.getFullYear() : d.getFullYear() + 1;
}

/**
 * The all-time figures behind the tiles.
 *
 * WIP is NOT simply time - fees billed - write-offs over every record: the
 * firm's invoices go back years before anyone recorded time in ClientZone, so
 * that sum is hugely negative for every long-standing client and useless for
 * deciding what to bill. WIP is the sum of byYear()'s per-year WIP, which
 * starts at the first fiscal year with time recorded -- see byYear().
 *
 * Pass clientsById where you have it: without it a write-off's fiscal year
 * cannot be derived and it is counted by date instead (see byYear()).
 */
export function wipMetrics({ timeEntries, invoices, writeOffs, clientsById = {} }) {
  const issued = issuedOnly(invoices);
  const counted = countedTimeEntries(timeEntries);
  const totalTime = counted.reduce((s, t) => s + num(t.billable_amount), 0);
  const totalFeesBilled = issued.reduce((s, i) => s + feesBilledOf(i), 0);
  const totalDiscount = issued.reduce((s, i) => s + num(i.discount), 0);
  const totalWriteOffs = (writeOffs || []).reduce((s, w) => s + num(w.amount), 0);
  const arBalance = issued.reduce((s, i) => s + arOf(i), 0);
  const rows = byYear({ timeEntries, invoices, writeOffs, clientsById });
  return {
    totalTime,
    totalFeesBilled,
    totalDiscount,
    totalInvoiced: totalFeesBilled - totalDiscount,
    totalWriteOffs,
    wip: yearTotals(rows).wip,
    arBalance,
  };
}

function sumTimeBy(entries, keyFn) {
  const map = {};
  for (const t of countedTimeEntries(entries)) {
    const name = keyFn(t) || 'Unknown';
    if (!map[name]) map[name] = { name, hours: 0, amount: 0 };
    map[name].hours += num(t.hours);
    map[name].amount += num(t.billable_amount);
  }
  return Object.values(map).sort((a, b) => b.amount - a.amount);
}

export function timeByEmployee(entries) {
  return sumTimeBy(entries, (t) => t.user_name || t.employee_name || t.created_by_name);
}

// "Category" in the spec == TimeEntry.time_code. The values ("balance sheet",
// "MTG", "Other", ...) are TimeCode rows in the database, surfaced verbatim.
// Nothing is hardcoded here.
export function timeByCode(entries) {
  return sumTimeBy(entries, (t) => t.time_code);
}

const emptyYear = (year) => ({
  year, hours: 0, time: 0,
  feesBilled: 0, discount: 0, invoiced: 0, disbursements: 0, tax: 0, total: 0,
  writeoffs: 0, ar: 0,
  timeEntries: [], invoices: [], writeOffEntries: [],
});

const hasNoFiscalYear = (row) => !row.year_end || Number.isNaN(Number(row.year_end));

const addInvoice = (row, inv) => {
  row.feesBilled += feesBilledOf(inv);
  row.discount += num(inv.discount);
  row.invoiced += invoicedOf(inv);
  row.disbursements += num(inv.disbursements);
  row.tax += taxOf(inv);
  row.total += num(totalOf(inv));
  row.ar += arOf(inv);
  row.invoices.push(inv);
};

/**
 * One row per fiscal year: the MasterFiles view of a client.
 *
 * Each row carries hours, time value, fees billed, discount, net fees
 * (`invoiced`), disbursements, tax, total invoiced, write-offs, A/R and
 * `wip`, plus the records behind them for drill-downs. Drafts are left out
 * entirely (isIssued).
 *
 *   wip = time - fees billed - write-offs
 *
 * The discount is NOT added back: it cleared WIP along with the fee it came
 * off, and shows in its own column.
 *
 * WIP STARTS AT THE FIRST FISCAL YEAR WITH TIME RECORDED. A year before that
 * holds only invoices for work ClientZone never saw, so its WIP is `null`
 * ("—"), not a large negative number, and it adds nothing to the total. A year
 * AFTER it counts in full even with no time of its own -- an invoice filed to
 * the year after the work still has to clear that work.
 *
 * Records with no fiscal year (blank year_end on time or invoices -- 64 of the
 * Lee Humble invoices -- or a write-off whose client is not in clientsById, so
 * its year cannot be derived) share one `year: null` row, sorted last, so the
 * money is visibly unfiled rather than missing. In that row an invoice or
 * write-off counts toward WIP only if it is dated on or after the first time
 * entry, the same history rule by date instead of by year.
 */
export function byYear({ timeEntries, invoices, writeOffs, clientsById = {} }) {
  const map = {};
  const bucket = (y) => {
    const k = y === null ? '__unfiled__' : y;
    if (!map[k]) map[k] = emptyYear(y);
    return map[k];
  };

  const counted = countedTimeEntries(timeEntries);
  for (const t of counted) {
    const row = bucket(hasNoFiscalYear(t) ? null : Number(t.year_end));
    row.hours += num(t.hours);
    row.time += num(t.billable_amount);
    row.timeEntries.push(t);
  }

  for (const inv of issuedOnly(invoices)) {
    addInvoice(bucket(hasNoFiscalYear(inv) ? null : Number(inv.year_end)), inv);
  }

  for (const w of writeOffs || []) {
    // Derived, not read: WriteOff has no year_end. Each write-off takes the
    // fiscal calendar of the client it belongs to, which in a group roll-up is
    // not necessarily the client whose page you are looking at. If that client
    // isn't in clientsById, guessing a calendar would silently mis-file the
    // money, so it goes to the unfiled row instead.
    const owner = clientsById[w.client];
    const y = owner ? yearEndForDate(w.date, owner.is_corporate, owner.fiscal_month_end) : null;
    const row = bucket(y);
    row.writeoffs += num(w.amount);
    row.writeOffEntries.push(w);
  }

  const timedYears = counted.filter((t) => !hasNoFiscalYear(t)).map((t) => Number(t.year_end));
  const firstYear = timedYears.length ? Math.min(...timedYears) : null;
  const dates = counted.map((t) => t.date).filter(Boolean).sort();
  const firstDate = dates.length ? dates[0] : null;
  const fromFirstDate = (r) => firstDate !== null && Boolean(r.date) && r.date >= firstDate;

  const wipOf = (r) => {
    if (r.year === null) {
      if (!counted.length) return null;
      const fees = r.invoices.filter(fromFirstDate).reduce((s, i) => s + feesBilledOf(i), 0);
      const wo = r.writeOffEntries.filter(fromFirstDate).reduce((s, w) => s + num(w.amount), 0);
      return r.time - fees - wo;
    }
    if (firstYear === null || r.year < firstYear) return null;
    return r.time - r.feesBilled - r.writeoffs;
  };

  return Object.values(map)
    .map((r) => ({ ...r, wip: wipOf(r) }))
    .sort((a, b) => {
      // The unfiled (null) row always sorts last, regardless of the
      // newest-first ordering of real years.
      if (a.year === null) return 1;
      if (b.year === null) return -1;
      return b.year - a.year;
    });
}

const SUMMED = [
  'hours', 'time', 'feesBilled', 'discount', 'invoiced', 'disbursements',
  'tax', 'total', 'writeoffs', 'ar',
];

/** The totals row under byYear()'s rows. `wip` sums only the years that have one. */
export function yearTotals(rows) {
  const out = Object.fromEntries(SUMMED.map((k) => [k, 0]));
  let wip = null;
  for (const r of rows || []) {
    for (const k of SUMMED) out[k] += num(r[k]);
    if (r.wip !== null && r.wip !== undefined) wip = (wip || 0) + r.wip;
  }
  return { ...out, wip: wip === null ? 0 : wip };
}

export function byClient({ clients, timeEntries, invoices, writeOffs, clientsById = {} }) {
  const own = (list, id) => (list || []).filter((r) => r.client === id);
  return (clients || [])
    .map((c) => {
      const t = yearTotals(byYear({
        timeEntries: own(timeEntries, c.id),
        invoices: own(invoices, c.id),
        writeOffs: own(writeOffs, c.id),
        clientsById,
      }));
      return {
        id: c.id, name: c.name, hours: t.hours, time: t.time,
        feesBilled: t.feesBilled, discount: t.discount, invoiced: t.invoiced,
        writeoffs: t.writeoffs, ar: t.ar, wip: t.wip,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

// What each metric tile is made of. Written once here so the drill-down cannot
// disagree with the tile it opened from, or with SummaryByYearTable's cells.
const TILE_SPECS = {
  time: {
    kind: 'time',
    rowsOf: (r) => r.timeEntries,
    totalOf: (r) => r.time,
  },
  invoiced: {
    kind: 'invoice',
    rowsOf: (r) => r.invoices,
    totalOf: (r) => r.invoiced,
  },
  writeoffs: {
    kind: 'writeoff',
    rowsOf: (r) => r.writeOffEntries,
    totalOf: (r) => r.writeoffs,
  },
  ar: {
    kind: 'invoice',
    rowsOf: (r) => r.invoices.filter((i) => arOf(i) !== 0),
    totalOf: (r) => r.ar,
  },
  wip: {
    kind: 'wip',
    rowsOf: (r) => ({ timeEntries: r.timeEntries, invoices: r.invoices, writeOffs: r.writeOffEntries }),
    totalOf: (r) => r.wip,
    // A year before time was recorded has no WIP (null) and adds nothing to
    // the tile, so it is not listed either.
    skip: (r) => r.wip === null || r.wip === undefined,
    isEmpty: (rows) => !rows.timeEntries.length && !rows.invoices.length && !rows.writeOffs.length,
  },
};

/**
 * The fiscal-year-grouped detail behind one MetricTiles tile.
 *
 * Returns { metric, kind, groups: [{ key, label, year, rows, total }] }, newest
 * year first and the unfiled bucket last, for <DetailModal> to render. The
 * group totals always add up to the tile's own figure from wipMetrics(),
 * because both are byYear()'s rows -- unfiled records included.
 *
 * A year that contributes nothing to THIS tile is omitted, so the write-offs
 * drill-down doesn't list every year the client merely existed in.
 */
export function tileDrilldown(tileKey, { yearRows }) {
  const spec = TILE_SPECS[tileKey];
  if (!spec) return { metric: tileKey, kind: null, groups: [] };

  const groups = (yearRows || [])
    .filter((r) => !(spec.skip && spec.skip(r)))
    .map((r) => {
      const unfiled = r.year === null || r.year === undefined;
      return {
        key: unfiled ? '__unfiled__' : String(r.year),
        label: unfiled ? 'No fiscal year' : `FY ${r.year}`,
        year: unfiled ? null : r.year,
        rows: spec.rowsOf(r),
        total: spec.totalOf(r),
        // Counted hours in that fiscal year, carried so a caller showing the
        // group heading does not have to re-sum the entries (and get a
        // different answer by forgetting countedTimeEntries). Always the hours
        // of the year's TIME, whichever tile was drilled into.
        hours: r.hours,
      };
    })
    .filter((g) => (spec.isEmpty ? !spec.isEmpty(g.rows) : g.rows.length > 0));

  return { metric: tileKey, kind: spec.kind, groups };
}
